import React, { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LoadingIndicator from '../LoadingIndicator';

// ============================================
// SECTION: For venues & destinations (B2B teaser)
// Deliberately placed as "slide 3": hero sets the mood, the promise
// section defines the product, then business visitors get their exit
// to /business before the deep B2C capability story begins.
// ============================================

// Card images live here; kickers/captions come from the "home" locale
// under bizTeaser.cards.<key>.
const BIZ_TEASER_CARDS = [
  { key: 'zoo', src: '/home/scan2play-zoo.webp' },
  { key: 'museum', src: '/home/scan2play-museum.webp' },
  { key: 'oldtown', src: '/home/scan2play-oldtown.webp' },
  { key: 'garden', src: '/home/moment-garden.webp' },
  { key: 'castle', src: '/home/moment-castle.webp' },
  { key: 'factory', src: '/home/moment-factory.webp' },
];

const BusinessTeaserSection = () => {
  const { t } = useTranslation('home');
  const stripRef = useRef(null);

  const scrollStrip = (dir) => {
    const el = stripRef.current;
    if (!el) return;
    const card = el.children[0];
    const step = card ? card.offsetWidth + 20 : 400;
    el.scrollBy({ left: dir * step, behavior: 'smooth' });
  };

  return (
  <section
    id="partners"
    className="relative bg-[#1a1a18] py-24 lg:py-32 px-6 scroll-mt-4"
  >
    <div className="max-w-[1400px] mx-auto">
      {/* Header block — the funnel opener, then the images do the talking */}
      <div className="flex items-end justify-between gap-6 mb-12 lg:mb-16">
        <div className="max-w-4xl">
          <p className="text-emerald-400 text-sm lg:text-lg font-bold tracking-wider mb-4 uppercase">
            {t('bizTeaser.kicker')}
          </p>
          <h2 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] mb-6">
            {t('bizTeaser.title')}
          </h2>
          <p className="text-base md:text-xl font-medium text-white/60 leading-relaxed">
            {t('bizTeaser.subtitle')}
          </p>
          <Link
            to="/business"
            className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-emerald-400 px-7 py-4 text-base font-bold text-[#1a1a18] shadow-lg shadow-emerald-500/20 transition-colors hover:bg-emerald-300 sm:w-auto"
          >
            {t('bizTeaser.ctaPrimary')}
            <span aria-hidden="true">→</span>
          </Link>
        </div>
        {/* Strip scroll controls — desktop only */}
        <div className="hidden md:flex gap-2 flex-shrink-0">
          {[
            [t('bizTeaser.scrollLeft'), -1, 'M15 19l-7-7 7-7'],
            [t('bizTeaser.scrollRight'), 1, 'M9 5l7 7-7 7'],
          ].map(([label, dir, path]) => (
            <button
              key={label}
              type="button"
              aria-label={label}
              onClick={() => scrollStrip(dir)}
              className="w-11 h-11 rounded-full border border-white/15 text-white/70 hover:text-white hover:border-white/40 transition-colors flex items-center justify-center"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d={path} />
              </svg>
            </button>
          ))}
        </div>
      </div>

      {/* Use-case strip — Scan to Play, shown not told. Horizontal
          snap-scroll on all breakpoints; the overflow hints there's
          more than tourism's usual suspects. */}
      <div ref={stripRef} className="flex gap-4 md:gap-5 overflow-x-auto snap-x snap-mandatory pb-3 -mx-6 px-6 scrollbar-thin">
        {BIZ_TEASER_CARDS.map(({ key, src }) => (
          <div
            key={key}
            className="relative flex-shrink-0 w-[78%] sm:w-[46%] md:w-[34%] lg:w-[26%] snap-start aspect-[4/5] rounded-3xl overflow-hidden border border-white/10"
          >
            <img
              src={src}
              alt={t('bizTeaser.cardAlt', { place: t(`bizTeaser.cards.${key}.kicker`) })}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
              <p className="text-emerald-400 text-xs font-bold tracking-wider uppercase mb-1.5">
                {t(`bizTeaser.cards.${key}.kicker`)}
              </p>
              <p className="text-white text-base md:text-lg font-semibold leading-snug">
                {t(`bizTeaser.cards.${key}.caption`)}
              </p>
            </div>
          </div>
        ))}

        {/* End tile — generalizes the strip and gives it an ending */}
        <Link
          to="/business"
          className="relative flex-shrink-0 w-[78%] sm:w-[46%] md:w-[34%] lg:w-[26%] snap-start aspect-[4/5] rounded-3xl border border-white/10 bg-white/5 hover:bg-white/10 transition-colors flex flex-col items-center justify-center text-center p-8 group"
        >
          <p className="text-white text-xl md:text-2xl font-bold leading-snug mb-3">
            {t('bizTeaser.endTitle')}
          </p>
          <p className="text-white/50 text-sm mb-6">
            {t('bizTeaser.endText')}
          </p>
          <span className="inline-flex items-center gap-2 text-emerald-400 font-semibold group-hover:gap-3 transition-all">
            {t('bizTeaser.endCta')} <span aria-hidden="true">→</span>
          </span>
        </Link>
      </div>

      {/* One chip row under the grid */}
      <div className="mt-8 flex flex-wrap gap-2.5">
        {t('bizTeaser.chips', { returnObjects: true }).map((chip) => (
          <span
            key={chip}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 bg-black/20 text-sm text-white/70 whitespace-nowrap"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400/80" />
            {chip}
          </span>
        ))}
      </div>

    </div>
  </section>
  );
};

// ============================================
// SECTION 0: Promise Section (FORKED for Nature B2C)
// ============================================

// The whole capability story of the deep-dive sections below, fused into
// one glanceable grid. Each entry maps to (and previews) a full section;
// titles/texts live in the "home" locale under promise.glance.<key>.
const GLANCE_CAPABILITIES = [
  { key: 'see', img: '/home/glance-see.webp' },
  { key: 'guide', img: '/home/glance-guide.webp' },
  { key: 'speak', img: '/home/cap-speak.webp' },
  { key: 'navigate', img: '/home/cap-navigate.webp' },
  { key: 'adapt', img: '/home/glance-adapt.webp' },
  { key: 'play', img: '/home/cap-play.webp' },
  { key: 'remember', img: '/home/feat-journal.webp' },
  { key: 'together', img: '/home/cap-together.webp' },
];

const PromiseSection = () => {
  const { t } = useTranslation('home');
  return (
    <section className="bg-[#1a1a18] py-24 lg:py-32">
      <div className="max-w-[1400px] mx-auto px-6">
        {/* Clay diorama — the promise as an image, before a word is read */}
        <img
          src="/home/glance-hero.webp"
          alt={t('promise.dioramaAlt')}
          loading="lazy"
          className="w-64 sm:w-80 lg:w-96 mx-auto mb-10 lg:mb-14 mix-blend-lighten"
        />
        <div className="max-w-4xl mx-auto md:text-center">
          {/* Main lead */}
          <p className="text-emerald-400 text-sm lg:text-lg font-bold tracking-wider mb-4">
            {t('promise.kicker')}
          </p>
          <h2 className="text-3xl sm:text-5xl lg:text-6xl max-w-2xl md:mx-auto font-bold text-white leading-[1.1] mb-6">
            {t('promise.titleLine1')}<br />
            {t('promise.titleLine2')}
          </h2>
          
          {/* Benefit statement */}
          <p className="text-base md:text-xl font-medium text-white/60 mb-14 lg:mb-20 max-w-2xl md:mx-auto">
            {t('promise.subtitle')}
          </p>
        </div>

        {/* Okio at a glance — the whole story of the sections below, compressed */}
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-y-8 md:gap-y-12 gap-x-16">
          {GLANCE_CAPABILITIES.map((cap) => (
            <div key={cap.key} className="flex items-center gap-5 md:gap-6">
              <img
                src={cap.img}
                alt=""
                loading="lazy"
                className="flex-shrink-0 w-20 h-20 md:w-24 md:h-24 mix-blend-lighten"
              />
              <div>
                <h3 className="text-white font-semibold text-lg mb-1">{t(`promise.glance.${cap.key}.title`)}</h3>
                <p className="text-white/45 text-[15px] leading-relaxed max-w-sm">{t(`promise.glance.${cap.key}.text`)}</p>
              </div>
            </div>
          ))}
        </div>

        {/* "See it in action" demos (NatureUseCaseShowcase) intentionally
            parked for now — component kept intact for possible revival. */}
      </div>
    </section>
  );
};

// ============================================
// SECTION: Agent chapter divider
// Marks the shift from the compact pitch (promise + business teaser)
// into the deep capability sections. The glance grid promised; from
// here on, the page proves.
// ============================================

const AgentIntroDivider = () => {
  const { t } = useTranslation('home');
  return (
  <section className="bg-gradient-to-b from-[#1D1D1F] to-black py-16 lg:py-24 overflow-hidden pt-24 lg:pt-36 px-6">
    <div className="max-w-3xl mx-auto text-center">
      <p className="text-emerald-400 text-sm lg:text-lg font-bold tracking-wider uppercase mb-4">
        {t('divider.kicker')}
      </p>
      {/* Chapter title outranks the section titles below it */}
      <h2 className="text-4xl sm:text-6xl lg:text-7xl font-bold text-white leading-[1.05] mb-6 tracking-tight">
        {t('divider.title')}
      </h2>
      {/* <p className="text-base md:text-xl font-medium text-white/60 max-w-2xl mx-auto">
        {t('divider.subtitle')}
      </p> */}
      {/* Eyes + logo lockup */}
      <div className="flex items-center gap-3 mt-20">
        <div className="scale-[4.8] md:scale-[5.4] origin-center transform-gpu mx-auto">
          <LoadingIndicator className="!mt-0 !py-0 !px-0" />
        </div>
      </div>
    </div>
    {/* The agent at work, in clay — she looks down into the valley, and the
        image dips over the seam into the first chapter below it. The blend
        keeps the overlap seamless on both backgrounds. */}
    {/* <img
      src="/home/agent-field.webp"
      alt={t('divider.sceneAlt')}
      loading="lazy"
      className="relative z-10 w-full max-w-2xl lg:max-w-3xl mx-auto mt-12 lg:mt-16 -mb-12 lg:-mb-16 mix-blend-lighten"
    /> */}
    
  </section>
  );
};

// ============================================
// SECTION: Chapter one of "Meet Okio" — the human-guide claim.
// Headline carries the category ("like a human guide, not a script on
// rails"); voice is the kicker/mechanism, not the message. Shown twice:
// a clay diorama (explorer + the floating mascot eyes mid-conversation)
// and a voice-native chat mock — spoken user bubble (waveform +
// transcript), guide bubble mid-narration. plan_real_guide_gaps.md, V.
// ============================================

// Static waveform for the "you spoke" bubble — hand-tuned bar heights so it
// reads as speech, not as a random equalizer.
const VOICE_WAVEFORM_HEIGHTS = [7, 12, 18, 10, 16, 20, 9, 14, 19, 12, 6, 10, 15, 8];

const VoiceSection = () => {
  const { t } = useTranslation('home');
  return (
    <section className="bg-black py-24 lg:py-32">
      <div className="max-w-[1400px] mx-auto px-6">
        {/* Header */}
        <div className="mb-12">
          <p className="text-emerald-400 text-sm lg:text-lg font-bold tracking-wider mb-4">
            {t('voice.kicker')}
          </p>
          <h2 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] mb-6">
            {t('voice.titleLine1')}<br />
            <span className="text-white/40">{t('voice.titleLine2')}</span>
          </h2>
          <p className="text-base md:text-xl font-medium text-white/60 max-w-2xl">
            {t('voice.subtitle')}
          </p>
        </div>

        {/* Diorama + conversation, side by side: the relationship on the
            left (guide as companion), the mechanics on the right (you
            speak — waveform bubble; the guide answers out loud — pulsing
            equalizer = "speaking right now") */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <img
            src="/home/voice-guide.webp"
            alt={t('voice.dioramaAlt')}
            loading="lazy"
            className="w-full max-w-xl mx-auto mix-blend-lighten"
          />
          <div className="w-full max-w-xl mx-auto lg:mx-0 space-y-6">
          <div className="flex justify-end">
            <div className="max-w-md bg-gray-700 rounded-2xl rounded-tr-sm px-5 py-4">
              <div className="flex items-center gap-2.5 mb-2.5">
                <svg
                  className="w-4 h-4 text-white/60 flex-shrink-0"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z" />
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                  <line x1="12" y1="19" x2="12" y2="22" />
                </svg>
                <div className="flex items-center gap-[3px] h-5" aria-hidden="true">
                  {VOICE_WAVEFORM_HEIGHTS.map((h, i) => (
                    <span
                      key={i}
                      className="w-[3px] rounded-full bg-white/50"
                      style={{ height: `${h}px` }}
                    />
                  ))}
                </div>
              </div>
              <p className="text-white">{t('voice.userLine')}</p>
            </div>
          </div>
          <div className="flex justify-start">
            <div className="max-w-2xl bg-gray-900 border border-gray-800 rounded-2xl rounded-tl-sm px-6 py-5">
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center">
                  <div className="flex items-end gap-[2.5px]" aria-hidden="true">
                    <span className="w-[3px] h-2 rounded-full bg-emerald-400 animate-pulse" />
                    <span className="w-[3px] h-3.5 rounded-full bg-emerald-400 animate-pulse [animation-delay:150ms]" />
                    <span className="w-[3px] h-2.5 rounded-full bg-emerald-400 animate-pulse [animation-delay:300ms]" />
                  </div>
                </div>
                <span className="text-emerald-400 text-sm font-medium">
                  {t('voice.guideSpeaking')}
                </span>
              </div>
              <p className="text-white/80 leading-relaxed">{t('voice.guideLine')}</p>
            </div>
          </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// SECTION 0.5: Guided Walkthrough Section (FORKED for Nature B2C)
// ============================================

// Images + step icons live here; labels/steps/alt come from the "home"
// locale under walkthrough.cases.<key>. Order matters: non-nature examples
// lead — the platform is broader than its nature roots.
const WALKTHROUGH_CASES = [
  { key: 'town', image: '/home/walkthrough-old-town.webp', icons: ['🏰', '🔎', '🧭', '📖'] },
  { key: 'museum', image: '/home/walkthrough-museum.webp', icons: ['🗺️', '🖼️', '✨', '🔎'] },
  { key: 'garden', image: '/home/walkthrough-garden-care.webp', icons: ['🌱', '✂️', '🪴', '💧'] },
  { key: 'mushroom', image: '/home/walkthrough-mushroom-foraging.webp', icons: ['📸', '🔎', '📚', '🍄'] },
  { key: 'vermi', image: '/home/walkthrough-vermicomposting.webp', icons: ['🪱', '🥬', '🌡️', '🌱'] },
];

const GuidedWalkthroughSection = () => {
  const { t } = useTranslation('home');
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [activeUseCaseIndex, setActiveUseCaseIndex] = useState(0);
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [hoveredStep, setHoveredStep] = useState(null);
  const [isPaused, setIsPaused] = useState(false);

  const useCases = WALKTHROUGH_CASES.map(({ key, image, icons }) => ({
    id: key,
    label: t(`walkthrough.cases.${key}.label`),
    image,
    imageAlt: t(`walkthrough.cases.${key}.alt`),
    steps: icons.map((icon, i) => ({
      id: i + 1,
      icon,
      label: t(`walkthrough.cases.${key}.steps.${i}`),
    })),
  }));

  const activeUseCase = useCases[activeUseCaseIndex];
  const steps = activeUseCase.steps;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  // Auto-advance steps, then cycle to next use case
  useEffect(() => {
    if (!isVisible || isPaused) return;
    
    const timer = setInterval(() => {
      setActiveStepIndex(prev => {
        const nextStep = prev + 1;
        if (nextStep >= steps.length) {
          setTimeout(() => {
            setActiveUseCaseIndex(prevUC => (prevUC + 1) % useCases.length);
            setActiveStepIndex(0);
          }, 600);
          return prev;
        }
        return nextStep;
      });
    }, 2400);
    
    return () => clearInterval(timer);
  }, [isVisible, isPaused, steps.length, useCases.length]);

  useEffect(() => {
    setActiveStepIndex(0);
  }, [activeUseCaseIndex]);

  const handleSelectUseCase = (index) => {
    setActiveUseCaseIndex(index);
    setActiveStepIndex(0);
    setIsPaused(true);
    setTimeout(() => setIsPaused(false), 12000);
  };

  return (
    <section ref={sectionRef} className="bg-black py-16 lg:py-24 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6">
        {/* Section header */}
        <div 
          className="text-left md:text-center mb-10 lg:mb-16"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.8s ease-out'
          }}
        >
          <p className="text-emerald-400 text-sm lg:text-lg font-bold tracking-wider mb-4">
            {t('walkthrough.kicker')}
          </p>
          <h2 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] mb-4">
            {t('walkthrough.titleLine1')}<br />{t('walkthrough.titleLine2')}
          </h2>
          <p className="text-base md:text-xl font-medium text-white/60 max-w-2xl md:mx-auto">
            {t('walkthrough.subtitle')}
          </p>
        </div>

        {/* Use Case Selector */}
        <div 
          className="flex flex-wrap items-center justify-start gap-3 mb-8 md:justify-center lg:mb-12"
          style={{
            opacity: isVisible ? 1 : 0,
            transition: 'opacity 0.8s ease-out 0.2s'
          }}
        >
          {useCases.map((uc, index) => (
            <button
              key={uc.id}
              onClick={() => handleSelectUseCase(index)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                index === activeUseCaseIndex
                  ? 'bg-white text-black'
                  : 'bg-white/10 text-white/60 hover:bg-white/20 hover:text-white'
              }`}
            >
              {uc.label}
            </button>
          ))}
        </div>

        {/* Main content: Four-panel image + Steps list */}
        <div className="grid grid-cols-1 lg:grid-cols-[1.6fr_1fr] gap-8 lg:gap-12 items-center max-w-6xl mx-auto">
          
          {/* First: Four-panel illustration */}
          <div 
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateX(0) scale(1)' : 'translateX(30px) scale(0.95)',
              transition: 'all 0.8s ease-out 0.4s'
            }}
          >
            <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-white/10 lg:scale-[1.03] transform-gpu">
              <img 
                src={activeUseCase.image}
                alt={activeUseCase.imageAlt}
                className="w-full aspect-[16/9] object-cover transition-opacity duration-500"
              />
              
              {/* Step highlight overlay - subtle glow on active quadrant */}
              <div className="absolute inset-0 grid grid-cols-2 grid-rows-2 pointer-events-none">
                {[0, 1, 2, 3].map((quadrant) => (
                  <div 
                    key={quadrant}
                    className={`transition-all duration-500 ${
                      quadrant === activeStepIndex
                        ? 'bg-emerald-500/10 ring-2 ring-inset ring-emerald-500/40'
                        : quadrant < activeStepIndex
                          ? 'bg-white/5'
                          : ''
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
          
          {/* Second: Step card */}
          <div 
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateX(0)' : 'translateX(-30px)',
              transition: 'all 0.8s ease-out 0.3s'
            }}
          >
            <div className="bg-gray-900/95 backdrop-blur-xl rounded-3xl shadow-2xl p-5 lg:p-6 border border-white/10">
              {/* Progress bar */}
              <div className="flex gap-1.5 mb-4">
                {steps.map((_, i) => (
                  <div 
                    key={i}
                    className={`h-1.5 flex-1 rounded-full transition-all duration-500 ${
                      i <= activeStepIndex ? 'bg-emerald-500' : 'bg-white/20'
                    }`}
                  />
                ))}
              </div>

              {/* Steps list */}
              <div className="space-y-1.5">
                {steps.map((step, i) => (
                  <button
                    key={step.id}
                    onClick={() => setActiveStepIndex(i)}
                    onMouseEnter={() => setHoveredStep(i)}
                    onMouseLeave={() => setHoveredStep(null)}
                    className={`w-full flex items-center gap-3 p-2.5 rounded-xl transition-all duration-300 text-left ${
                      i === activeStepIndex 
                        ? 'bg-emerald-500/20 border-2 border-emerald-500' 
                        : i < activeStepIndex
                          ? 'bg-white/5'
                          : 'bg-white/5 hover:bg-white/10'
                    }`}
                    style={{
                      transform: hoveredStep === i ? 'scale(1.02)' : 'scale(1)'
                    }}
                  >
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs transition-all ${
                      i < activeStepIndex 
                        ? 'bg-emerald-500 text-white' 
                        : i === activeStepIndex
                          ? 'bg-emerald-500 text-white ring-4 ring-emerald-500/30'
                          : 'bg-white/20 text-white/60'
                    }`}>
                      {i < activeStepIndex ? '✓' : step.icon}
                    </div>
                    <span className={`text-sm font-medium ${
                      i < activeStepIndex ? 'text-white/40 line-through' : 'text-white'
                    }`}>
                      {step.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// AR Overlay Guidance Section (FORKED for Nature B2C)
// ============================================

// Images live here; labels/alts come from the "home" locale under
// ar.cases.<key>. Non-nature examples lead (see WALKTHROUGH_CASES note).
const AR_CASES = [
  { key: 'landmark', image: '/home/ar-overlay-landmark.webp' },
  { key: 'city', image: '/home/ar-overlay-city-tour.webp' },
  { key: 'plant', image: '/home/ar-overlay-plant-disease.webp' },
  { key: 'tracking', image: '/home/ar-overlay-animal-tracking.webp' },
  { key: 'mushroom', image: '/home/ar-overlay-mushroom-id.webp' },
];

const AROverlayGuidanceSection = () => {
  const { t } = useTranslation('home');
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [activeUseCaseIndex, setActiveUseCaseIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const useCases = AR_CASES.map(({ key, image }) => ({
    id: key,
    label: t(`ar.cases.${key}.label`),
    image,
    imageAlt: t(`ar.cases.${key}.alt`),
  }));

  const activeUseCase = useCases[activeUseCaseIndex];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  // Auto-cycle use cases
  useEffect(() => {
    if (!isVisible || isPaused) return;
    
    const timer = setInterval(() => {
      setActiveUseCaseIndex(prev => (prev + 1) % useCases.length);
    }, 4000);
    
    return () => clearInterval(timer);
  }, [isVisible, isPaused, useCases.length]);

  const handleSelectUseCase = (index) => {
    setActiveUseCaseIndex(index);
    setIsPaused(true);
    setTimeout(() => setIsPaused(false), 8000);
  };

  return (
    <section ref={sectionRef} className="bg-black py-16 lg:py-20 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6">
        {/* Header */}
        <div 
          className="text-left md:text-center mb-8 lg:mb-12"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.8s ease-out'
          }}
        >
          <p className="text-emerald-400 text-sm lg:text-lg font-bold tracking-wider mb-4">
            {t('ar.kicker')}
          </p>
          <h2 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] mb-4">
            {t('ar.title')}
          </h2>
          <p className="text-base md:text-xl font-medium text-white/60 max-w-2xl md:mx-auto">
            {t('ar.subtitle')}
          </p>
        </div>

        {/* Hero visual */}
        <div className="relative">
          {/* AR Image */}
          <div 
            className="relative rounded-3xl overflow-hidden shadow-2xl"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'scale(1)' : 'scale(0.95)',
              transition: 'all 1s ease-out 0.2s'
            }}
          >
            <img 
              src={activeUseCase.image}
              alt={activeUseCase.imageAlt}
              className="w-full aspect-[4/3] sm:aspect-[16/9] object-cover transition-opacity duration-500"
            />
            
            {/* Simple AR Badge */}
            <div className="absolute top-4 left-4 sm:top-5 sm:left-5 px-3 py-1.5 bg-black/60 backdrop-blur-sm rounded-full border border-white/20 flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-white/90 text-xs font-medium">{t('ar.badge')}</span>
            </div>
            
            {/* Gradient fade at bottom - desktop only */}
            <div className="hidden sm:block absolute inset-x-0 bottom-0 h-1/4 bg-gradient-to-t from-black/40 to-transparent" />
          </div>

          {/* Use Case Selector - below image */}
          <div 
            className="flex flex-wrap items-center justify-start gap-3 mt-4 md:justify-center"
            style={{
              opacity: isVisible ? 1 : 0,
              transition: 'opacity 0.8s ease-out 0.3s'
            }}
          >
            {useCases.map((uc, index) => (
              <button
                key={uc.id}
                onClick={() => handleSelectUseCase(index)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  index === activeUseCaseIndex
                    ? 'bg-white text-black'
                    : 'bg-white/10 text-white/60 hover:bg-white/20 hover:text-white'
                }`}
              >
                {uc.label}
              </button>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
};

// ============================================
// Expert Knowledge Section (FORKED for Nature B2C)
// ============================================

const ExpertKnowledgeSection = () => {
  const { t } = useTranslation('home');
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [activeExample, setActiveExample] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const [learnMoreExpanded, setLearnMoreExpanded] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  // Toggle learn more every 3.5 seconds
  useEffect(() => {
    if (!isVisible) return;
    const timer = setInterval(() => {
      setLearnMoreExpanded(prev => !prev);
    }, 3500);
    return () => clearInterval(timer);
  }, [isVisible]);

  // Images/spotlights live here; names/domains/tags/learn-more texts come
  // from the "home" locale under expert.examples.<key>. Heritage example
  // leads (see WALKTHROUGH_CASES note).
  const examples = [
    {
      key: 'clock',
      image: '/home/astronomical-clock-spotlight.webp',
      tagColor: 'cyan',
      spotlight: { top: '8%', left: '40%', width: '40%', height: '76%' },
    },
    {
      key: 'deceiver',
      image: '/home/amethyst-deceiver-spotlight.webp',
      tagColor: 'emerald',
      spotlight: { top: '15%', left: '48%', width: '28%', height: '75%' },
    },
    {
      key: 'beetle',
      image: '/home/tree-bark-canker-spotlight.webp',
      tagColor: 'amber',
      spotlight: { top: '5%', left: '28%', width: '44%', height: '90%' },
    },
    {
      key: 'fachwerk',
      image: '/home/half-timbered-house-spotlight.webp',
      tagColor: 'cyan',
      spotlight: { top: '8%', left: '16%', width: '54%', height: '84%' },
    },
    {
      key: 'blight',
      image: '/home/tomato-blight-spotlight.webp',
      tagColor: 'rose',
      spotlight: { top: '12%', left: '28%', width: '38%', height: '72%' },
    },
  ].map((ex) => ({
    ...ex,
    name: t(`expert.examples.${ex.key}.name`),
    latin: t(`expert.examples.${ex.key}.latin`),
    domain: t(`expert.examples.${ex.key}.domain`),
    tag: t(`expert.examples.${ex.key}.tag`),
    learnMore: t(`expert.examples.${ex.key}.learnMore`),
  }));

  // Auto-cycle examples (pause on hover) - collapse learn more first
  useEffect(() => {
    if (!isVisible || isHovering) return;
    const timer = setInterval(() => {
      setLearnMoreExpanded(false); // Collapse first
      setTimeout(() => {
        setActiveExample(prev => (prev + 1) % examples.length);
      }, 300); // Wait for collapse animation
    }, 4000);
    return () => clearInterval(timer);
  }, [isVisible, isHovering, examples.length]);

  const current = examples[activeExample];

  return (
    <section ref={sectionRef} className="bg-black py-24 lg:py-32 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6">
        
        {/* Split layout: Giant stat left, visual right */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          
          {/* Left: Typography-forward headline + stats */}
          <div 
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateX(0)' : 'translateX(-40px)',
              transition: 'all 0.8s ease-out'
            }}
          >
            <p className="text-emerald-400 text-sm lg:text-lg font-bold tracking-wider mb-4">
              {t('expert.kicker')}
            </p>
            <h2 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] mb-6">
              {t('expert.titleLine1')}<br />
              <span className="text-white/40">{t('expert.titleLine2')}</span>
            </h2>
            <p className="text-base md:text-xl font-medium text-white/60 mb-12 max-w-lg">
              {t('expert.subtitle')}
            </p>

            {/* Domain switcher badges */}
            <div 
              className="flex flex-wrap items-center gap-3"
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
                transition: 'all 0.6s ease-out 0.4s'
              }}
            >
              {examples.map((ex, i) => (
                <button
                  key={i}
                  onClick={() => setActiveExample(i)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    i === activeExample 
                      ? 'bg-white text-black' 
                      : 'bg-white/10 text-white/60 hover:bg-white/20 hover:text-white'
                  }`}
                >
                  {ex.domain}
                </button>
              ))}
              <span className="text-sm text-white/40 italic">
                {t('expert.andMore')}
              </span>
            </div>
          </div>
          
          {/* Right: The visual card */}
          <div 
            className="relative"
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateX(0) scale(1)' : 'translateX(40px) scale(0.95)',
              transition: 'all 0.8s ease-out 0.2s'
            }}
          >
            {/* Floating glow behind card */}
            <div className="absolute -inset-4 bg-white/5 rounded-[3rem] blur-2xl" />
            
            {/* Main card - fixed height, info slides up over image */}
            {/* Taller on mobile (3/4) to give room for learn more, shorter on desktop (4/3) */}
            <div className="relative bg-gray-900 rounded-3xl overflow-hidden shadow-2xl border border-white/10 group aspect-[3/4] sm:aspect-[4/3]">
              {/* Image - always full size */}
              <img 
                src={current.image}
                alt={current.name}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              
              {/* Spotlight box overlay - matches app style, behind info bar */}
              <div 
                className="absolute border-2 border-white rounded-2xl"
                style={{ 
                  top: current.spotlight.top,
                  left: current.spotlight.left,
                  width: current.spotlight.width,
                  height: current.spotlight.height,
                  boxShadow: '0 0 60px rgba(255,255,255,0.3), inset 0 0 30px rgba(255,255,255,0.1)',
                  animation: 'pulse 2s ease-in-out infinite',
                  transition: 'all 0.5s ease-out'
                }}
              />
              
              {/* Tag badge */}
              <div className={`absolute top-4 right-4 px-3 py-1.5 rounded-full text-xs font-bold bg-${current.tagColor}-500 text-white shadow-lg z-10`}>
                {current.tag}
              </div>

              {/* Info bar - slides up from bottom, overlays image and spotlight */}
              <div 
                className="absolute bottom-0 left-0 right-0 bg-gray-900/95 backdrop-blur-md p-4 sm:p-6 transition-all duration-500 ease-out z-20"
              >
                {/* Title row with learn more button */}
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-lg sm:text-2xl font-bold text-white mb-0.5 sm:mb-1">{current.name}</h3>
                    <p className="text-white/50 italic text-sm sm:text-base">{current.latin}</p>
                  </div>
                  <button 
                    onClick={() => setLearnMoreExpanded(!learnMoreExpanded)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all flex-shrink-0 ${
                      learnMoreExpanded 
                        ? 'bg-emerald-500 text-white' 
                        : 'bg-white/10 text-white/70 hover:bg-white/20'
                    }`}
                  >
                    {learnMoreExpanded ? t('expert.less') : t('expert.learnMore')}
                  </button>
                </div>

                {/* Learn more content - appears below title when expanded */}
                <div 
                  className="grid transition-all duration-500 ease-out"
                  style={{
                    gridTemplateRows: learnMoreExpanded ? '1fr' : '0fr',
                  }}
                >
                  <div className="overflow-hidden">
                    <div className="pt-3 sm:pt-4 relative">
                      <p className="text-white/80 text-xs sm:text-sm leading-relaxed">
                        {current.learnMore}
                      </p>
                      {/* Fade out gradient to indicate more content */}
                      <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-gray-900 to-transparent pointer-events-none" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

// ============================================
// Adaptive Planning Section (COPY AS-IS from original)
// ============================================

const AdaptivePlanningSection = () => {
  const { t } = useTranslation('home');
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [showUpdates, setShowUpdates] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          // Stagger the plan updates
          setTimeout(() => setShowUpdates(true), 600);
        }
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const updates = [
    { type: 'new', color: 'emerald', icon: '+', label: t('planning.updates.added.label'), title: t('planning.updates.added.title'), reason: t('planning.updates.added.reason') },
    { type: 'modified', color: 'amber', icon: '↻', label: t('planning.updates.updated.label'), title: t('planning.updates.updated.title'), reason: t('planning.updates.updated.reason') },
    { type: 'done', color: 'gray', icon: '✓', label: t('planning.updates.done.label'), title: t('planning.updates.done.title'), reason: null },
  ];

  return (
    <section ref={sectionRef} className="bg-gradient-to-b from-[#1D1D1F] to-black py-24 lg:py-32 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6">
        
        {/* Before/After transformation narrative */}
        <div className="max-w-4xl mx-auto md:text-center mb-16">
          <div 
            className="mb-8"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
              transition: 'all 0.8s ease-out'
            }}
          >
            <p className="text-emerald-400 text-sm lg:text-lg font-bold tracking-wider mb-4">
              {t('planning.kicker')}
            </p>
            
            <h2 className="text-3xl sm:text-5xl lg:text-6xl max-w-2xl md:mx-auto font-bold text-white leading-[1.1]">
              {t('planning.title')}
            </h2>
          </div>
          
          <p 
            className="text-base md:text-xl font-medium text-white/60 max-w-3xl mx-auto"
            style={{
              opacity: isVisible ? 1 : 0,
              transition: 'opacity 0.8s ease-out 0.3s'
            }}
          >
            {t('planning.subtitle')}
          </p>
        </div>

        {/* Plan Update Card - animated in */}
        <div className="max-w-lg mx-auto">
          <div 
            className="bg-gray-900/80 backdrop-blur-xl rounded-3xl border border-white/10 overflow-hidden shadow-2xl"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0) scale(1)' : 'translateY(40px) scale(0.95)',
              transition: 'all 0.8s ease-out 0.2s'
            }}
          >
            {/* Header */}
            <div className="px-6 py-5 border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
                  <svg className="w-6 h-6 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-white font-semibold text-lg">{t('planning.cardTitle')}</h3>
                  <p className="text-white/50 text-sm">{t('planning.cardSubtitle')}</p>
                </div>
              </div>
            </div>
            
            {/* Staggered update items */}
            <div className="p-5 space-y-3">
              {updates.map((update, i) => (
                <div 
                  key={i}
                  className={`rounded-xl p-4 transition-all duration-500 ${
                    update.type === 'new' ? 'bg-emerald-500/10 border border-emerald-500/30' :
                    update.type === 'modified' ? 'bg-amber-500/10 border border-amber-500/30' :
                    'bg-white/5 border border-white/10'
                  }`}
                  style={{
                    opacity: showUpdates ? 1 : 0,
                    transform: showUpdates ? 'translateX(0)' : 'translateX(-20px)',
                    transition: `all 0.5s ease-out ${i * 0.15}s`
                  }}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                      update.type === 'new' ? 'bg-emerald-500 text-white' :
                      update.type === 'modified' ? 'bg-amber-500 text-white' :
                      'bg-white/20 text-white/60'
                    }`}>
                      {update.icon}
                    </div>
                    <div className="flex-1">
                      <p className={`text-xs font-bold uppercase tracking-wider mb-1 ${
                        update.type === 'new' ? 'text-emerald-400' :
                        update.type === 'modified' ? 'text-amber-400' :
                        'text-white/40'
                      }`}>
                        {update.label}
                      </p>
                      <p className={`font-medium ${update.type === 'done' ? 'text-white/50 line-through' : 'text-white'}`}>
                        {update.title}
                      </p>
                      {update.reason && (
                        <p className="text-white/40 text-sm mt-1">{update.reason}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

// ============================================
// Contextual Awareness Section (FORKED for Nature B2C)
// ============================================

const ContextualAwarenessSection = () => {
  const { t } = useTranslation('home');
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  // Selection is index-based so it survives a language switch mid-session.
  const [selectedTools, setSelectedTools] = useState([0]);
  const [selectedLevel, setSelectedLevel] = useState(1);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const tools = t('context.tools', { returnObjects: true });
  const levels = t('context.levels', { returnObjects: true });

  const toggleTool = (index) => {
    setSelectedTools(prev =>
      prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
    );
  };

  return (
    <section ref={sectionRef} className="bg-black py-24 lg:py-32 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6">
        
        {/* Split layout: Message left, Card right */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          
          {/* Left: The proactive agent narrative */}
          <div
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateX(0)' : 'translateX(-40px)',
              transition: 'all 0.8s ease-out'
            }}
          >
            <p className="text-emerald-400 text-sm lg:text-lg font-bold tracking-wider mb-4">
              {t('context.kicker')}
            </p>
            
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] mb-6">
              {t('context.titleLine1')}<br />
              <span className="text-white/40">{t('context.titleLine2')}</span>
            </h2>
            
            <p className="text-base md:text-xl font-medium text-white/60 mb-8 max-w-md">
              {t('context.subtitle')}
            </p>

            {/* Context signals */}
            <div className="flex flex-wrap gap-3">
              {[
                { icon: '📍', label: t('context.signalLocation'), active: true },
                { icon: '🌿', label: t('context.signalItems', { count: selectedTools.length }), active: selectedTools.length > 0 },
                { icon: '📊', label: levels[selectedLevel], active: true },
                { icon: '⏱', label: t('context.signalTime'), active: false },
              ].map((signal, i) => (
                <div 
                  key={i}
                  className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm transition-all ${
                    signal.active 
                      ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' 
                      : 'bg-white/5 text-white/40 border border-white/10'
                  }`}
                >
                  <span>{signal.icon}</span>
                  <span>{signal.label}</span>
                  {signal.active && <span className="text-amber-500">✓</span>}
                </div>
              ))}
            </div>
          </div>
          
          {/* Right: Background image with form card */}
          <div
            className="relative"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateX(0) scale(1)' : 'translateX(40px) scale(0.95)',
              transition: 'all 0.8s ease-out 0.2s'
            }}
          >
            {/* Gardening tools background image */}
            <div className="rounded-3xl overflow-hidden">
              <img 
                src="/home/gardening-tools-background.png"
                alt={t('context.bgAlt')}
                className="w-full aspect-[16/10] object-cover object-center"
              />
            </div>
              
            {/* Interactive form card - below image on mobile, floating overlay on desktop */}
            <div className="relative mt-4 lg:mt-0 lg:absolute lg:top-1/2 lg:right-8 lg:-translate-y-1/2 w-full lg:w-80">
                <div className="bg-black/80 backdrop-blur-xl rounded-2xl border border-emerald-500/30 overflow-hidden shadow-2xl"
                  style={{ boxShadow: '0 0 60px rgba(16, 185, 129, 0.2)' }}
                >
                  {/* Form header */}
                  <div className="px-5 py-4 border-b border-white/10">
                    <h3 className="text-white font-semibold">{t('context.formTitle')}</h3>
                  </div>
                  
                  {/* Interactive form */}
                  <div className="p-5 space-y-4">
                    {/* Tools - clickable pills */}
                    <div>
                      <label className="text-white/60 text-xs block mb-2">{t('context.gearLabel')}</label>
                      <div className="flex flex-wrap gap-2">
                        {tools.slice(0, 5).map((tool, index) => (
                          <button 
                            key={tool}
                            onClick={() => toggleTool(index)}
                            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                              selectedTools.includes(index)
                                ? 'bg-emerald-500 text-white' 
                                : 'bg-white/10 text-white/70 hover:bg-white/20'
                            }`}
                          >
                            {selectedTools.includes(index) ? '✓ ' : ''}{tool}
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    {/* Experience level */}
                    <div>
                      <label className="text-white/60 text-xs block mb-2">{t('context.levelLabel')}</label>
                      <div className="flex gap-2">
                        {levels.map((level, i) => (
                          <button 
                            key={level}
                            onClick={() => setSelectedLevel(i)}
                            className={`flex items-center gap-1.5 text-xs transition-all ${
                              i === selectedLevel ? 'text-emerald-400' : 'text-white/50'
                            }`}
                          >
                            <div className={`w-3 h-3 rounded-full border ${
                              i === selectedLevel 
                                ? 'border-emerald-400 bg-emerald-400' 
                                : 'border-white/30'
                            }`} />
                            {level}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  {/* Continue button */}
                  <div className="px-5 pb-5">
                    <button className="w-full py-2.5 bg-emerald-500 hover:bg-emerald-400 text-white text-sm font-medium rounded-lg transition-colors">
                      {t('context.continue')}
                    </button>
                  </div>
                </div>
              </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// Location Intelligence Section (COPY AS-IS from original)
// ============================================

const LocationIntelligenceSection = () => {
  const { t } = useTranslation('home');
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredPin, setHoveredPin] = useState(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  // Procedural pins with positions; names/types from the "home" locale
  const pins = [
    { id: 1, key: 'chanterelle', distance: '200m', x: '25%', y: '40%' },
    { id: 2, key: 'oak', distance: '400m', x: '50%', y: '55%' },
    { id: 3, key: 'chapel', distance: '800m', x: '72%', y: '35%' },
  ].map((pin) => ({
    ...pin,
    name: t(`location.pins.${pin.key}.name`),
    type: t(`location.pins.${pin.key}.type`),
  }));

  return (
    <section ref={sectionRef} className="bg-black py-24 lg:py-32 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6">
        
        {/* Header */}
        <div 
          className="text-center mb-12"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.8s ease-out'
          }}
        >
          <p className="text-cyan-400 text-sm lg:text-lg font-bold tracking-wider mb-4">
            {t('location.kicker')}
          </p>
          <h2 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] mb-6">
            {t('location.titleLine1')}<br />
            <span className="text-cyan-400">{t('location.titleLine2')}</span>
          </h2>
          <p className="text-base md:text-xl font-medium text-white/60 max-w-2xl md:mx-auto">
            {t('location.subtitle')}
          </p>
        </div>

        {/* Map card with hybrid approach */}
        <div 
          className="relative max-w-4xl mx-auto"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'scale(1)' : 'scale(0.95)',
            transition: 'all 0.8s ease-out 0.2s'
          }}
        >
          <div className="bg-gray-900 rounded-3xl border border-gray-800 overflow-hidden shadow-2xl">
            {/* Map area - satellite background with procedural pins */}
            <div className="relative aspect-[21/9] overflow-hidden">
              {/* Satellite map background */}
              <img 
                src="/home/satellite-map-forest.jpg"
                alt={t('location.mapAlt')}
                className="absolute inset-0 w-full h-full object-cover"
              />
              
              {/* Dashed route line connecting pins */}
              <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 5 }}>
                <path 
                  d="M 25% 40% L 50% 55% L 72% 35%"
                  stroke="rgba(6, 182, 212, 0.6)"
                  strokeWidth="3"
                  strokeDasharray="8 6"
                  fill="none"
                  strokeLinecap="round"
                />
              </svg>
              
              {/* Current location indicator */}
              <div 
                className="absolute"
                style={{ left: '18%', top: '55%', zIndex: 10 }}
              >
                <div className="relative">
                  <div className="w-4 h-4 bg-blue-500 rounded-full border-2 border-white shadow-lg" />
                  <div className="absolute inset-0 w-4 h-4 bg-blue-500 rounded-full animate-ping opacity-50" />
                </div>
              </div>
              
              {/* Procedural pins */}
              {pins.map((pin, i) => (
                <div 
                  key={pin.id}
                  className="absolute cursor-pointer group"
                  style={{ 
                    left: pin.x, 
                    top: pin.y, 
                    zIndex: hoveredPin === pin.id ? 20 : 10,
                    opacity: isVisible ? 1 : 0,
                    transform: isVisible ? 'scale(1)' : 'scale(0)',
                    transition: `all 0.5s ease-out ${0.3 + i * 0.1}s`
                  }}
                  onMouseEnter={() => setHoveredPin(pin.id)}
                  onMouseLeave={() => setHoveredPin(null)}
                >
                  {/* Pin marker */}
                  <div className={`
                    w-10 h-10 -ml-5 -mt-5 rounded-full flex items-center justify-center 
                    transition-all duration-300 shadow-lg
                    ${hoveredPin === pin.id 
                      ? 'bg-cyan-400 scale-125 shadow-cyan-500/50' 
                      : 'bg-cyan-500 hover:bg-cyan-400'}
                  `}>
                    <span className="text-white font-bold text-sm">{pin.id}</span>
                  </div>
                  
                  {/* Tooltip on hover */}
                  <div className={`
                    absolute left-8 top-1/2 -translate-y-1/2 
                    bg-black/90 backdrop-blur-xl rounded-xl px-4 py-3 
                    whitespace-nowrap border border-white/10
                    transition-all duration-200
                    ${hoveredPin === pin.id ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2 pointer-events-none'}
                  `}>
                    <p className="text-white font-medium">{pin.name}</p>
                    <p className="text-white/50 text-sm">{pin.distance} • {pin.type}</p>
                  </div>
                </div>
              ))}
              
              {/* Badge overlay */}
              <div className="absolute top-4 left-4 bg-black/60 backdrop-blur-sm rounded-lg px-3 py-2" style={{ zIndex: 15 }}>
                <p className="text-white text-sm font-medium">{t('location.badge')}</p>
              </div>
              
              {/* Bottom gradient */}
              <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-gray-900 to-transparent" style={{ zIndex: 5 }} />
            </div>
            
            {/* Location list */}
            <div className="p-6 space-y-3">
              {pins.map((pin, i) => (
                <div 
                  key={pin.id} 
                  className={`
                    flex items-center gap-4 p-4 rounded-xl cursor-pointer transition-all
                    ${hoveredPin === pin.id ? 'bg-gray-800 scale-[1.02]' : 'bg-gray-800/50 hover:bg-gray-800'}
                  `}
                  style={{
                    opacity: isVisible ? 1 : 0,
                    transform: isVisible ? 'translateX(0)' : 'translateX(-20px)',
                    transition: `all 0.5s ease-out ${0.4 + i * 0.1}s`
                  }}
                  onMouseEnter={() => setHoveredPin(pin.id)}
                  onMouseLeave={() => setHoveredPin(null)}
                >
                  <div className={`
                    w-10 h-10 rounded-full flex items-center justify-center transition-colors
                    ${hoveredPin === pin.id ? 'bg-cyan-500' : 'bg-cyan-500/20'}
                  `}>
                    <span className={`font-bold ${hoveredPin === pin.id ? 'text-white' : 'text-cyan-400'}`}>{pin.id}</span>
                  </div>
                  <div className="flex-1">
                    <p className="text-white font-medium">{pin.name}</p>
                    <p className="text-white/50 text-sm">{pin.type}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-white/70 text-sm">{pin.distance}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// Observation Logging Section (COPY AS-IS from original)
// ============================================

const ObservationLoggingSection = () => {
  const { t } = useTranslation('home');
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.3 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  // Photo pins with thumbnail images
  const photoPins = [
    { x: '20%', y: '35%', img: '/home/photo1.jpeg' },
    { x: '45%', y: '55%', img: '/home/photo2.jpeg' },
    { x: '70%', y: '30%', img: '/home/photo4.jpeg' },
    { x: '55%', y: '70%', img: '/home/photo3.jpeg' },
  ];

  return (
    <section ref={sectionRef} className="bg-black py-16 lg:py-20 border-t border-white/5">
      <div className="max-w-[1400px] mx-auto px-6">
        <div 
          className="flex flex-col lg:flex-row items-center justify-center gap-8 lg:gap-12"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'all 0.6s ease-out'
          }}
        >
          {/* Map with photo pins - larger, with satellite background */}
          <div className="relative w-full lg:w-[480px] aspect-[16/9] rounded-2xl overflow-hidden border border-white/10 flex-shrink-0 shadow-xl">
            {/* Satellite map background */}
            <img 
              src="/home/satellite-map-meadow.webp"
              alt={t('observation.mapAlt')}
              className="absolute inset-0 w-full h-full object-cover opacity-70"
            />
            
            {/* Slight dark overlay for pin visibility */}
            <div className="absolute inset-0 bg-black/20" />
            
            {/* Photo pins */}
            {photoPins.map((pin, i) => (
              <div 
                key={i}
                className="absolute"
                style={{ 
                  left: pin.x, 
                  top: pin.y,
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? 'scale(1)' : 'scale(0)',
                  transition: `all 0.5s ease-out ${0.3 + i * 0.1}s`
                }}
              >
                {/* Photo thumbnail pin */}
                <div className="relative -ml-5 -mt-5 group cursor-pointer">
                  <div className="w-10 h-10 rounded-lg overflow-hidden border-2 border-white shadow-lg transition-transform group-hover:scale-110">
                    <img 
                      src={pin.img} 
                      alt="Observation" 
                      className="w-full h-full object-cover"
                    />
                  </div>
                  {/* Pin pointer */}
                  <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-white rotate-45 shadow" />
                </div>
              </div>
            ))}
            
            {/* Current location */}
            <div 
              className="absolute"
              style={{ left: '30%', top: '50%' }}
            >
              <div className="relative -ml-2 -mt-2">
                <div className="w-4 h-4 bg-blue-500 rounded-full border-2 border-white shadow-lg" />
                <div className="absolute inset-0 w-4 h-4 bg-blue-500 rounded-full animate-ping opacity-40" />
              </div>
            </div>
          </div>
          
          {/* Text content */}
          <div className="w-full lg:w-auto text-center">
            <h3 className="text-2xl sm:text-3xl font-bold text-white mb-3">
              {t('observation.title')}
            </h3>
            <p className="text-white/50 text-lg mb-6 max-w-md mx-auto">
              {t('observation.text')}
            </p>
            
            {/* Shared discoveries hint */}
            <div 
              className="inline-flex items-center gap-3 px-4 py-3 bg-white/5 rounded-xl border border-white/10"
              style={{
                opacity: isVisible ? 1 : 0,
                transition: 'opacity 0.6s ease-out 0.5s'
              }}
            >
              <div className="flex -space-x-2">
                <div className="w-7 h-7 rounded-full overflow-hidden border-2 border-gray-800">
                  <img src="/home/photo1.jpeg" alt="" className="w-full h-full object-cover" />
                </div>
                <div className="w-7 h-7 rounded-full overflow-hidden border-2 border-gray-800">
                  <img src="/home/photo2.jpeg" alt="" className="w-full h-full object-cover" />
                </div>
                <div className="w-7 h-7 rounded-full overflow-hidden border-2 border-gray-800">
                  <img src="/home/photo4.jpeg" alt="" className="w-full h-full object-cover" />
                </div>
              </div>
              <span className="text-white/50 text-sm">{t('observation.shared')}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// Long-term Memory Section (COPY AS-IS from original)
// ============================================

const LongTermMemorySection = () => {
  const { t } = useTranslation('home');
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [animatedSessions, setAnimatedSessions] = useState(0);
  const [animatedSpecies, setAnimatedSpecies] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  // Animate numbers
  useEffect(() => {
    if (!isVisible) return;
    const sessionTimer = setInterval(() => {
      setAnimatedSessions(prev => Math.min(prev + 1, 47));
    }, 30);
    const speciesTimer = setInterval(() => {
      setAnimatedSpecies(prev => Math.min(prev + 1, 23));
    }, 60);
    return () => {
      clearInterval(sessionTimer);
      clearInterval(speciesTimer);
    };
  }, [isVisible]);

  return (
    <section ref={sectionRef} className="bg-gradient-to-b from-[#1D1D1F] to-black py-24 lg:py-32 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6">
        
        {/* Hero stats - THE visual anchor */}
        <div 
          className="text-center mb-16"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
            transition: 'all 0.8s ease-out'
          }}
        >

          <div className="text-center mb-16">
            <p className="text-emerald-400 text-sm lg:text-lg font-bold tracking-wider mb-4">
              {t('memory.kicker')}
            </p>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] mb-6">
              {t('memory.titleLine1')}<br />
              <span className="text-white/40">{t('memory.titleLine2')}</span>
            </h2>
            <p className="text-xl text-white/60 max-w-2xl mx-auto">
              {t('memory.subtitle')}
            </p>
          </div>
        </div>

        <div className="max-w-3xl mx-auto space-y-6">
          <div className="flex justify-end">
            <div className="max-w-lg bg-gray-700 rounded-2xl rounded-tr-sm px-5 py-4">
              <p className="text-white">
                {t('memory.userMessage')}
              </p>
            </div>
          </div>
          <div className="flex justify-start">
            <div className="max-w-2xl bg-gray-900 border border-gray-800 rounded-2xl rounded-tl-sm px-6 py-5">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-full bg-rose-500/20 flex items-center justify-center">
                  <span className="text-rose-400 text-sm">🧠</span>
                </div>
                <span className="text-rose-400 text-sm font-medium">{t('memory.analysisLabel')}</span>
              </div>
              <div className="space-y-4 text-white/80">
                <p><strong className="text-white">{t('memory.healthLabel')}</strong> {t('memory.healthText')}</p>
                <p><strong className="text-white">{t('memory.trendsLabel')}</strong> {t('memory.trendsText')}</p>
                <p><strong className="text-white">{t('memory.recoLabel')}</strong> {t('memory.recoText')}</p>
              </div>
              <div className="mt-4 pt-4 border-t border-gray-800 flex gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-white">47</p>
                  <p className="text-white/50 text-xs">{t('memory.statSessions')}</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-white">23</p>
                  <p className="text-white/50 text-xs">{t('memory.statSpecies')}</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-white">5</p>
                  <p className="text-white/50 text-xs">{t('memory.statMonths')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// Collaboration Section (COPY AS-IS from original)
// ============================================

// Images live here; titles/descriptions come from the "home" locale under
// collab.features.<key>.
const collaborationFeatures = [
  { id: 1, key: 'quest', image: '/home/collab-realtime-sync.webp' },
  { id: 2, key: 'chat', image: '/home/collab-live-chat.webp' },
  { id: 3, key: 'community', image: '/home/collab-community.webp' },
];

const CollaborationSection = () => {
  const { t } = useTranslation('home');
  const features = collaborationFeatures.map((feature) => ({
    ...feature,
    title: t(`collab.features.${feature.key}.title`),
    description: t(`collab.features.${feature.key}.description`),
  }));
  return (
    <section className="bg-black py-24 lg:py-32">
      <div className="max-w-[1400px] mx-auto px-6">
        {/* Header */}
        <div className="mb-12">
          <p className="text-emerald-400 text-sm lg:text-lg font-bold tracking-wider mb-4">
            {t('collab.kicker')}
          </p>
          <h2 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] mb-6">
            {t('collab.titleLine1')}<br />
            <span className="text-white/40">{t('collab.titleLine2')}</span>
          </h2>
          <p className="text-base md:text-xl font-medium text-white/60 max-w-2xl">
            {t('collab.subtitle')}
          </p>
        </div>

        {/* Responsive grid cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature) => (
            <div key={feature.id}>
              <div className="rounded-3xl overflow-hidden h-[400px] sm:h-[450px] relative group">
                {/* Image */}
                <img
                  src={feature.image}
                  alt={feature.title}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                
                {/* Text at bottom */}
                <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 via-black/40 to-transparent">
                  <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
                  <p className="text-white/60 text-sm">{feature.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================

const NatureCapabilityShowcase = () => {
  return (
    <div>
      <PromiseSection />
      <BusinessTeaserSection />
      <AgentIntroDivider />
      <VoiceSection />
      <GuidedWalkthroughSection />
      <AROverlayGuidanceSection />
      <ExpertKnowledgeSection />
      <AdaptivePlanningSection />
      <ContextualAwarenessSection />
      <LocationIntelligenceSection />
      <ObservationLoggingSection />
      <LongTermMemorySection />
      <CollaborationSection />
    </div>
  );
};

export default NatureCapabilityShowcase;
